// Выбор пользователя с поиском по ФИО и почте (ux.md, раздел 5): «Чья роль» у администратора.
// Поиск — GET /api/users на каждое нажатие с короткой задержкой; архивные не предлагаются.
// Заместителя выбирают из кандидатов без почты — DeputySearchField (FR-018б). Список не
// загрузился — не «Никого не нашлось», а ошибка с кодом в списке и выноска под полем с причиной,
// номером операции и «Повторить» (T135); на время повтора выноска на месте со спиннером (T168).
import type { UserListItem } from '@kp/contracts';
import { SearchPickerField } from '@kp/ui';
import type { SearchPickerOption } from '@kp/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { Ref } from 'react';
import { apiQuery } from '../../api/client.ts';
import { ListLoadError, failedListText, useQueryFailure } from '../load-error.tsx';

const FAILED = 'Не удалось загрузить список пользователей';

const SEARCH_DELAY_MS = 200;
const LIMIT = 20;

export interface UserSearchFieldProps {
  label: string;
  value: SearchPickerOption | null;
  onChange: (option: SearchPickerOption, user: UserListItem) => void;
  /** Кого не предлагать. */
  excludeId?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  /** Первое поле окна — фокус при открытии. */
  autoFocus?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function userOption(
  user: Pick<UserListItem, 'id' | 'fullName' | 'email'>,
): SearchPickerOption {
  return { value: user.id, label: user.fullName, description: user.email };
}

export function UserSearchField({
  label,
  value,
  onChange,
  excludeId,
  error,
  hint,
  required,
  autoFocus,
  ref,
}: UserSearchFieldProps) {
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setQ(query.trim()), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [query]);
  const users = useQuery({
    ...apiQuery('users.list', { query: { q: q || undefined, limit: LIMIT } }),
    placeholderData: keepPreviousData,
  });
  const { failure, retrying, retry } = useQueryFailure(users);
  // Пока сбой на месте (и на время его повтора), варианты прежнего поиска не показываются.
  const found = (failure ? [] : (users.data?.items ?? [])).filter(
    (user) => user.id !== excludeId && user.state !== 'archived',
  );
  return (
    <>
      <SearchPickerField
        ref={ref}
        label={label}
        placeholder="Выберите пользователя"
        searchPlaceholder="Найти по ФИО или почте"
        emptyText={
          failure ? failedListText(FAILED, failure) : 'Никого не нашлось. Проверьте раскладку'
        }
        value={value}
        query={query}
        onQueryChange={setQuery}
        options={found.map(userOption)}
        loading={users.isPending || (users.isFetching && found.length === 0)}
        onChange={(option) => {
          const user = found.find((item) => item.id === option.value);
          if (user) onChange(option, user);
        }}
        error={error}
        hint={hint}
        required={required}
        autoFocus={autoFocus}
      />
      {failure ? (
        <ListLoadError what={FAILED} view={failure} retrying={retrying} onRetry={retry} />
      ) : null}
    </>
  );
}
